import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FadeUp } from "@/components/Reveal";
import { useI18n } from "@/lib/i18n";
import DispatchCountdown from "@/components/DispatchCountdown";

export default function OrderNowCTA({ products = [] }) {
  const { t, lang } = useI18n();
  const nl = lang === "nl";
  const de = lang === "de";
  const liveBadge = nl ? "LIVE · VANDAAG NOG VERZONDEN" : de ? "LIVE · HEUTE VERSANDT" : "LIVE · SHIPS TODAY";
  const beforeCET = nl ? "Voor 15:00 CET" : de ? "Bis 15:00 CET" : "Before 3PM CET";
  const trust = nl
    ? ["✓ Gratis verzending > €200", "✓ HPLC ≥ 99% zuiverheid", "✓ Betaal veilig via Stripe", "✓ Discreet verpakt"]
    : de
    ? ["✓ Kostenloser Versand > €200", "✓ HPLC ≥ 99% Reinheit", "✓ Sicher zahlen via Stripe", "✓ Diskret verpackt"]
    : ["✓ Free shipping > €200", "✓ HPLC ≥ 99% purity", "✓ Secure payment via Stripe", "✓ Discreet packaging"];
  return (
    <section
      data-testid="order-now"
      className="relative overflow-hidden py-24 md:py-32 bg-[#EFEBE4]"
    >
      <motion.div
        className="absolute top-10 -left-32 w-[560px] h-[560px] pointer-events-none"
        style={{
          background: "radial-gradient(circle at center, rgba(0,82,155,0.10), transparent 65%)",
          filter: "blur(70px)",
        }}
        animate={{ scale: [1, 1.15, 1] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-0 right-0 w-[600px] h-[600px] pointer-events-none"
        style={{
          background: "radial-gradient(circle at center, rgba(209,42,47,0.10), transparent 65%)",
          filter: "blur(80px)",
        }}
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />

      <div
        className="absolute inset-0 pointer-events-none opacity-[0.08]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(-8deg, transparent, transparent 60px, rgba(17,17,17,0.5) 60px, rgba(17,17,17,0.5) 61px)",
        }}
      />

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 relative z-10">
        {/* Section overline */}
        <FadeUp>
          <p className="font-mono-label text-[11px] text-neutral-600 mb-6">
            [ 03 · {t("order_now_over").toUpperCase()} ]
          </p>
        </FadeUp>

        {/* Signature "Bestel nu" CTA — now ABOVE the products */}
        <FadeUp delay={0.15}>
          <div className="relative mb-14 md:mb-20">
            {/* pulsing glow behind card */}
            <motion.div
              className="absolute -inset-6 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at 20% 50%, rgba(0,82,155,0.30), transparent 55%), radial-gradient(circle at 80% 50%, rgba(209,42,47,0.20), transparent 55%)",
                filter: "blur(40px)",
              }}
              animate={{ opacity: [0.55, 0.95, 0.55] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />

            <div className="relative border-2 border-black bg-white p-8 md:p-14">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-10">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="inline-flex items-center gap-2 bg-emerald-600 text-white px-3 py-1 font-mono-label text-[10px]">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                      {liveBadge}
                    </span>
                    <span className="font-mono-label text-[10px] text-neutral-500 hidden md:inline">
                      {beforeCET}
                    </span>
                  </div>

                  <p className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
                    <span className="block">{t("order_now_title_1")}</span>
                    <span className="block italic text-neutral-500 text-3xl md:text-4xl mt-1">
                      {t("order_now_title_2")}
                    </span>
                  </p>

                  <p className="mt-6 max-w-lg text-neutral-700 leading-relaxed">
                    {t("order_now_sub")}
                  </p>

                  <div className="mt-6">
                    <DispatchCountdown variant="default" />
                  </div>
                </div>

                {/* Right-sized CTA button (was way too big) */}
                <Link
                  to="/shop"
                  data-testid="order-now-cta"
                  className="group relative overflow-hidden bg-black text-white flex-shrink-0"
                >
                  <motion.span
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(circle at 30% 50%, rgba(0,82,155,0.55), transparent 60%)",
                    }}
                    animate={{ opacity: [0.55, 1, 0.55] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  />
                  <span className="relative flex items-center gap-3 px-8 py-4">
                    <span className="font-mono-label text-[11px] tracking-[0.2em]">
                      {t("order_now_cta")}
                    </span>
                    <motion.span
                      className="text-base"
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                    >
                      →
                    </motion.span>
                  </span>
                </Link>
              </div>

              {/* Trust row inside CTA card */}
              <div className="mt-8 pt-6 border-t border-neutral-200 grid grid-cols-2 md:grid-cols-4 gap-4 font-mono-label text-[10px] text-neutral-600">
                {trust.map((b, i) => <span key={i}>{b}</span>)}
              </div>
            </div>
          </div>
        </FadeUp>

        {/* Trust row */}
        <FadeUp delay={0.2}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              t("order_now_bullet_1"),
              t("order_now_bullet_2"),
              t("order_now_bullet_3"),
              t("order_now_bullet_4"),
            ].map((b, i) => (
              <div key={i} className="border-t border-neutral-400 pt-3 flex items-baseline gap-3">
                <span className="font-mono-label text-[10px] text-neutral-600">0{i + 1}</span>
                <span className="font-body text-sm text-neutral-800">{b}</span>
              </div>
            ))}
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
